import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowLeft, KeyRound, ShieldCheck, AlertTriangle, History, Search, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function InstructorUnlock() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/admin/unlock-history?limit=25");
      setHistory(data.history || []);
    } catch {
      // History fetch failed; UI shows empty state
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedReason = reason.trim();
    if (!trimmedEmail || !trimmedReason || trimmedReason.length < 3) {
      toast.error("Email and reason (≥3 chars) are required");
      return;
    }
    setSubmitting(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/unlock-student", {
        email: trimmedEmail,
        reason: trimmedReason,
        send_email: sendEmail,
      });
      setLastResult(data);
      if (data.was_already_paid) {
        toast.info(`${data.full_name} already had access — no change.`);
      } else {
        toast.success(`${data.full_name} unlocked${data.email_sent ? " · email sent" : ""}`);
      }
      setEmail("");
      setReason("");
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="instructor-unlock-page">
        <Link to="/instructor" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="unlock-back-link">
          <ArrowLeft className="w-4 h-4" /> Cohort dashboard
        </Link>

        <div className="overline mb-2"><span className="red-bar" />Instructor tools</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-2" data-testid="unlock-title">
          Manual student unlock
        </h1>
        <p className="text-zinc-600 mb-8 max-w-2xl text-sm leading-relaxed">
          Rescue students who paid through Stripe but didn't get automatic access — webhook delays, key mismatches, abandoned-then-paid checkouts. Idempotent and audit-logged.
        </p>

        {/* Warning notice */}
        <div className="border-l-4 border-amber-500 bg-amber-50 p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-zinc-800 leading-relaxed">
              <strong>Verify the Stripe charge first.</strong> Use this only after confirming the
              student's card was actually charged. Reason field should reference the Stripe charge
              ID (<code className="font-mono bg-amber-100 px-1 py-0.5 text-[11px]">ch_...</code> or
              <code className="font-mono bg-amber-100 px-1 py-0.5 text-[11px] ml-1">cs_...</code>)
              or last 4 of the card. Every unlock is logged below.
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="card-tech p-6 mb-10" data-testid="unlock-form">
          <div className="grid md:grid-cols-2 gap-5">
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Student email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jenn.spen@icloud.com"
                required
                className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm font-mono bg-white"
                data-testid="unlock-email-input"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Reason / receipt ref</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="ch_3PqXyZ... or 'Stripe live charge $149 5/21'"
                required
                minLength={3}
                maxLength={500}
                className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm font-mono bg-white"
                data-testid="unlock-reason-input"
              />
            </label>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 accent-red-600"
              data-testid="unlock-send-email-checkbox"
            />
            <Mail className="w-4 h-4 text-zinc-500" />
            Send "you're unlocked" email via Resend
          </label>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
              data-testid="unlock-submit-btn"
            >
              <KeyRound className="w-4 h-4" />
              {submitting ? "Unlocking…" : "Unlock student"}
            </button>
            {lastResult && (
              <div className="text-xs font-mono text-zinc-600" data-testid="unlock-last-result">
                {lastResult.was_already_paid
                  ? `Already had access · ${lastResult.email}`
                  : <><CheckCircle2 className="w-3.5 h-3.5 inline text-green-600" /> Unlocked · {lastResult.email} {lastResult.email_sent && "· email sent"}</>}
              </div>
            )}
          </div>
        </form>

        {/* Audit log */}
        <section data-testid="unlock-history-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline mb-1"><span className="red-bar" />Audit log</div>
              <h2 className="font-heading text-xl font-bold tracking-tight inline-flex items-center gap-2">
                <History className="w-5 h-5 text-zinc-500" /> Recent manual unlocks
              </h2>
            </div>
          </div>
          {historyLoading ? (
            <div className="text-sm text-zinc-500 italic">Loading audit log…</div>
          ) : history.length === 0 ? (
            <div className="text-sm text-zinc-500 italic" data-testid="unlock-history-empty">
              No manual unlocks recorded. This log shows every time you (or another instructor) unlock a student account.
            </div>
          ) : (
            <div className="border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">When</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Student</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Reason</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">By</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b border-zinc-100 last:border-b-0" data-testid={`unlock-history-row-${h.id}`}>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600 whitespace-nowrap">
                        {new Date(h.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{h.target_email}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-700 max-w-md">{h.reason}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-600">{h.performed_by_email}</td>
                      <td className="px-3 py-2 text-[11px]">
                        {h.was_already_paid ? (
                          <span className="text-zinc-500">Was already paid</span>
                        ) : (
                          <span className="text-green-700 font-semibold inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Granted
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
